import classNames from 'classnames';
import * as _ from 'lodash-es';
import { AnimatePresence, motion } from 'motion/react';
import type { ReactNode } from 'react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Location } from 'react-router';
import { useLocation } from 'react-router';
import { useEffectEvent } from 'use-effect-event';
import * as styles from './RightPanel.module.scss';
import Button from '../../components/Button';
import IconButton from '../../components/IconButton';
import StickyHeader from '../../components/StickyHeader';
import SVGIcon from '../../components/SVGIcon';
import closeIcon from '../../icons/non-prefilled/16x16/clear.svg';
import { SHARE_SECRET_PARAM_KEY } from '../../post/useShareSecret';
import useIsLayoutSize from '../../post/utils/useIsLayoutSize';
import {
  LAYOUT_MIN_MAX_WIDTHS,
  LayoutSize,
} from '../../post/utils/useLayoutSize';
import addTestId from '../../utils/addTestId';
import Logger from '../../utils/Logger';
import useMainContentWidthSelector from '../../utils/useMainContentWidthSelector';

const ANIMATION_DURATION_MS = parseInt(styles.layoutAnimationDurationMs, 10);
// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const RIGHT_PANEL_WIDTH = parseInt(styles.rightPanelWidth, 10);
const RIGHT_PANEL_MOBILE_WIDTH = parseInt(styles.rightPanelMobileWidth, 10);
const RESIZE_MAX_WIDTH = parseInt(styles.resizeMaxWidth, 10);
const BORDER_WIDTH = parseInt(styles.borderWidth, 10);

type RightPanelState = {
  node: JSX.Element;
  width: number | null;
  isPinned: boolean;
  config: {
    name?: string;
    isResizable?: boolean;
    initialWidth?: number;
  };
};

type RightPanelStateContextType = {
  node: JSX.Element | null;
  name: string | null;
};

const RightPanelStateContext =
  React.createContext<RightPanelStateContextType | null>(null);

type RightPanelFeaturesContextType = {
  isPinnable: boolean;
  isResizable: boolean;
};

const RightPanelFeaturesContext =
  React.createContext<RightPanelFeaturesContextType | null>(null);

const RightPanelWidthContext = React.createContext<number | null>(null);
// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const RightPanelPinnedWidthContext = React.createContext<number | null>(
  null,
);

type RightPanelActionsContextType = {
  openRightPanel: (
    node: JSX.Element,
    config?: RightPanelState['config'],
  ) => void;
  closeRightPanel: () => void;
  setIsPinned: (isPinned: boolean) => void;
  resize: (width: number) => void;
};

const RightPanelActionsContext =
  React.createContext<RightPanelActionsContextType | null>(null);

const getLocationPropsForChange = (location: Location) => {
  const searchParams = new URLSearchParams(location.search);
  searchParams.delete(SHARE_SECRET_PARAM_KEY);

  return {
    pathname: location.pathname,
    search: searchParams.toString(),
  } as const;
};

interface RightPanelProviderProps {
  children: React.ReactNode;
  initialRightPanel?: JSX.Element;
}

const RightPanelProvider = ({
  children,
  initialRightPanel,
}: RightPanelProviderProps) => {
  const [rightPanelState, setRightPanelState] =
    useState<RightPanelState | null>(
      initialRightPanel
        ? {
            node: initialRightPanel,
            width: null,
            config: {},
            isPinned: false,
          }
        : null,
    );

  const { isPinnableWidth: isPinnable, isResizableWidth } =
    useMainContentWidthSelector((width) => ({
      isPinnableWidth:
        width >=
        // 916px (desktop breakpoint) + 337px (right panel) = 1253px
        LAYOUT_MIN_MAX_WIDTHS[LayoutSize.desktop][0] + RIGHT_PANEL_WIDTH,
      isResizableWidth:
        width >=
        // 916px (desktop breakpoint) + 544px (resize max width) = 1460px
        LAYOUT_MIN_MAX_WIDTHS[LayoutSize.desktop][0] + RESIZE_MAX_WIDTH,
    }));

  const isResizable =
    !!rightPanelState?.config?.isResizable && isResizableWidth;
  const isPinned = !!rightPanelState?.isPinned && isPinnable;
  const isMobile = useIsLayoutSize(LayoutSize.mobile);
  const isRightPanelOpen = !!rightPanelState?.node;

  const width = useMemo(() => {
    if (!isRightPanelOpen) return 0;
    if (isMobile) return RIGHT_PANEL_MOBILE_WIDTH;
    if (!isResizable) return RIGHT_PANEL_WIDTH;
    return rightPanelState?.width ?? RIGHT_PANEL_WIDTH;
  }, [isResizable, rightPanelState?.width, isMobile, isRightPanelOpen]);

  // having the pinned width in a separate context like this makes sure that any consumer
  // of the pinned width only rerender when the right panel is actually pinned.
  const pinnedWidth = isPinned ? width : null;

  const actionsContext = useMemo((): RightPanelActionsContextType => {
    return {
      openRightPanel: (
        node: JSX.Element,
        config?: RightPanelState['config'],
      ) => {
        setRightPanelState({
          node,
          width: config?.initialWidth ?? null,
          config: config ?? {},
          isPinned: false,
        });
      },
      closeRightPanel: () => setRightPanelState(null),
      resize: (nextWidth: number) => {
        setRightPanelState((state) => {
          if (!state) return state;
          return { ...state, width: nextWidth };
        });
      },
      setIsPinned: (isPinnedNext: boolean) => {
        setRightPanelState((state) => {
          if (!state) return state;
          return { ...state, isPinned: isPinnedNext };
        });
      },
    };
  }, []);

  const stateContext = useMemo(
    (): RightPanelStateContextType => ({
      node: rightPanelState?.node ?? null,
      name: rightPanelState?.config.name ?? null,
    }),
    [rightPanelState?.node, rightPanelState?.config.name],
  );

  const featuresContext = useMemo(
    (): RightPanelFeaturesContextType => ({
      isPinnable,
      isResizable,
    }),
    [isPinnable, isResizable],
  );

  const location = useLocation();
  const previousLocationRef = useRef(getLocationPropsForChange(location));

  useEffect(() => {
    const currentLocation = getLocationPropsForChange(location);

    if (
      !isPinned &&
      !location.state?.keepRightPanelOpen &&
      !_.isEqual(previousLocationRef.current, currentLocation)
    ) {
      setRightPanelState(null);
    }
    previousLocationRef.current = currentLocation;
  }, [location, isPinned]);

  return (
    <RightPanelStateContext.Provider value={stateContext}>
      <RightPanelActionsContext.Provider value={actionsContext}>
        <RightPanelFeaturesContext.Provider value={featuresContext}>
          <RightPanelWidthContext.Provider value={width}>
            <RightPanelPinnedWidthContext.Provider value={pinnedWidth}>
              {children}
            </RightPanelPinnedWidthContext.Provider>
          </RightPanelWidthContext.Provider>
        </RightPanelFeaturesContext.Provider>
      </RightPanelActionsContext.Provider>
    </RightPanelStateContext.Provider>
  );
};

const useCheckedContext = <Context,>(context: React.Context<Context>) => {
  const value = useContext(context);
  const hasContext = !!value;
  useEffect(() => {
    if (!hasContext) {
      Logger.error('RightPanel must be used within a RightPanelProvider');
    }
  }, [hasContext]);

  return value;
};

export const useRightPanel = () => {
  const context = useCheckedContext(RightPanelActionsContext);

  const openRightPanel = useMemo(
    () => context?.openRightPanel ?? (() => {}),
    [context?.openRightPanel],
  );

  const closeRightPanel = useMemo(
    () => context?.closeRightPanel ?? (() => {}),
    [context?.closeRightPanel],
  );

  return {
    openRightPanel,
    closeRightPanel,
  };
};

export const useRightPanelPinnedWidth = () =>
  useContext(RightPanelPinnedWidthContext);
export const useRightPanelWidth = () => useContext(RightPanelWidthContext) ?? 0;

export const useRightPanelPinnedState = () => {
  const { isPinnable } = useCheckedContext(RightPanelFeaturesContext) ?? {
    isPinnable: false,
  };
  const actions = useCheckedContext(RightPanelActionsContext);
  const pinnedWidth = useRightPanelPinnedWidth();

  const setIsRightPanelPinned = useMemo(
    () => actions?.setIsPinned ?? (() => {}),
    [actions?.setIsPinned],
  );

  return {
    isRightPanelPinnable: isPinnable,
    isRightPanelPinned: pinnedWidth != null,
    setIsRightPanelPinned,
  };
};

export const useCurrentRightPanelName = () => {
  const { name } = useCheckedContext(RightPanelStateContext) ?? {
    name: null,
  };
  return name;
};

const RightPanelRoot = () => {
  const { node } = useCheckedContext(RightPanelStateContext) ?? {
    node: null,
  };
  const { resize } = useCheckedContext(RightPanelActionsContext) ?? {
    resize: () => {},
  };
  const { isResizable } = useCheckedContext(RightPanelFeaturesContext) ?? {
    isResizable: false,
  };
  const { isRightPanelPinned } = useRightPanelPinnedState();
  const width = useRightPanelWidth();

  const isOpen = !!node;
  const rootRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const startWidthRef = useRef<number | null>(width);
  const deltaRef = useRef(0);
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const resizeEvent = useEffectEvent(resize);

  useEffect(() => {
    if (!isResizing || isMobile || !isResizable) return undefined;

    const handleResizeEnd = () => setIsResizing(false);
    const handleResizeMove = (event: MouseEvent) => {
      const startWidth = startWidthRef.current;
      if (!startWidth) return;

      deltaRef.current += event.movementX;

      const nextWidth = Math.min(
        RESIZE_MAX_WIDTH,
        Math.max(startWidth - deltaRef.current, RIGHT_PANEL_WIDTH),
      );
      resizeEvent(nextWidth);
    };

    document.addEventListener('mouseup', handleResizeEnd);
    document.addEventListener('mousemove', handleResizeMove);

    return () => {
      document.removeEventListener('mouseup', handleResizeEnd);
      document.removeEventListener('mousemove', handleResizeMove);
    };
  }, [isResizing, resize, isMobile, isResizable]);

  const handleResizeStart = useCallback(() => {
    const element = rootRef.current;
    if (!element) return;

    deltaRef.current = 0;
    startWidthRef.current = element.clientWidth;
    setIsResizing(true);
  }, []);

  return (
    <div className={styles.rightPanelRootContainer}>
      <div
        data-no-print
        ref={rootRef}
        className={classNames(styles.rightPanelRoot, {
          [styles.isOpen]: isOpen,
          [styles.isResizing]: isResizing,
          [styles.isPinned]: isRightPanelPinned,
        })}
        style={{ width }}
      >
        {isOpen && isResizable && handleResizeStart && (
          <Button
            className={styles.resizeHandle}
            onMouseDown={handleResizeStart}
          />
        )}

        {/* Keep the right panel content visible during close animation */}
        <AnimatePresence>
          {!!node && (
            <motion.div
              key="view-container"
              exit={{ display: 'none' }}
              transition={{ duration: ANIMATION_DURATION_MS / 1000 }}
              className={styles.rightPanelRootContent}
              style={{ width: width - BORDER_WIDTH }}
            >
              {node}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

interface InnerProps {
  children?: ReactNode;
  footer?: ReactNode;
  header: ReactNode;
}

const Inner = ({ children, footer, header }: InnerProps) => {
  return (
    <div
      className={styles.rightPanelInnerContainer}
      {...addTestId('rightPanel')}
    >
      <div className={styles.rightPanelInner}>
        {header}
        <div className={styles.rightPanelInnerContent}>{children}</div>
      </div>
      {!!footer && <div>{footer}</div>}
    </div>
  );
};

type RightPanelIconButton = Omit<
  React.ComponentProps<typeof Button>,
  'children' | 'className'
> & {
  icon: string;
  active?: boolean;
};

const RightPanelIconButton = React.forwardRef<
  HTMLButtonElement,
  RightPanelIconButton
>(({ icon, active, ...rest }, ref) => {
  return (
    <Button
      {...rest}
      className={classNames(styles.iconButton, { [styles.isActive]: active })}
      ref={ref}
    >
      <SVGIcon size={12}>{icon}</SVGIcon>
    </Button>
  );
});

interface NewHeader {
  title: React.ReactNode;
  iconButtonsExtra?: React.ReactNode;
}

const NewHeader = ({ title, iconButtonsExtra }: NewHeader) => {
  const { closeRightPanel } = useRightPanel();
  return (
    <StickyHeader>
      <div className={styles.rightPanelNewHeader}>
        <h4>{title}</h4>
        <div className={styles.iconButtons}>
          {iconButtonsExtra}
          <Button
            className={styles.iconButton}
            onClick={closeRightPanel}
            aria-label="Close right panel"
          >
            <SVGIcon size={12}>{closeIcon}</SVGIcon>
          </Button>
        </div>
      </div>
    </StickyHeader>
  );
};

interface HeaderProps {
  title: string;
  controls?: ReactNode;
}

const Header = ({ title, controls }: HeaderProps) => {
  const { closeRightPanel } = useRightPanel();

  return (
    <StickyHeader>
      <div className={styles.rightPanelHeader}>
        <h4>{title}</h4>
        {!!controls && <div className={styles.headerControls}>{controls}</div>}
        <IconButton
          className={styles.closeButton}
          onClick={closeRightPanel}
          aria-label="Close right panel"
        >
          {closeIcon}
        </IconButton>
      </div>
    </StickyHeader>
  );
};

const RightPanelPinnedPlaceholder = () => {
  const width = useRightPanelPinnedWidth();
  if (!width) return null;
  return <div style={{ width }} />;
};

export default {
  Header,
  NewHeader,
  Root: RightPanelRoot,
  Provider: RightPanelProvider,
  Inner,
  IconButton: RightPanelIconButton,
  PinnedPlaceholder: RightPanelPinnedPlaceholder,
};
